import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import notify from '../../../../../utils/notify';
import {
  DateCountdown,
  SubmitConfirmModal,
} from '../../../../../shared/offer-domain/ui';
import {
  CLARIFICATION_PROGRESS,
  isIcClarificationEditable,
  isIcClarificationLocked,
} from '../../../../../shared/offer-domain/clarification';
import {
  OfferingTab,
  DocumentAttachmentTab,
  useClarificationProgressActions,
} from '../../../te-bridge';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { SkeletonText } from '../../../../../Components/Common/Skeleton';

const MENU_TABS = [
  {
    id: 'offering',
    label: 'Offering',
    icon: 'ri-store-2-line',
  },
  {
    id: 'document-attachment',
    label: 'Document Attachment',
    icon: 'ri-file-text-line',
  },
];

const Clarification = ({
  procurementId,
  procurement,
  isLoadingProcurement = false,
  statusClarificationProgress: statusProgressProp = null,
  onProgressChange = null,
  procurementMitraId: procurementMitraIdProp = null,
  idOffer: idOfferProp = null,
  isLoadingIdentity: isLoadingIdentityProp = false,
  onRefreshProcurement = null,
}) => {
  const [activeTab, setActiveTab] = useState('offering');
  const [localProgress, setLocalProgress] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [highlightArmed, setHighlightArmed] = useState(false);
  const [focusRespondId, setFocusRespondId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  const pendingBySourceRef = useRef({ offering: [], document: [] });
  const [pendingReady, setPendingReady] = useState({
    offering: false,
    document: false,
  });
  const [livePendingTargets, setLivePendingTargets] = useState([]);
  const procurementMitraId = procurementMitraIdProp;
  const idOffer = idOfferProp;
  const isLoadingIdentity = isLoadingIdentityProp;
  const { savingAction, isSaving, submitIcClarification } =
    useClarificationProgressActions({ idOffer, procurementId });

  const handlePendingFrom = useCallback((source, targets) => {
    pendingBySourceRef.current[source] = targets ?? [];
    setPendingReady(prev =>
      prev[source] ? prev : { ...prev, [source]: true }
    );
    const next = [
      ...(pendingBySourceRef.current.offering ?? []),
      ...(pendingBySourceRef.current.document ?? []),
    ];
    setLivePendingTargets(prev => {
      if (
        prev.length === next.length &&
        prev.every((item, index) => item.id === next[index]?.id)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleOfferingPending = useCallback(
    targets => handlePendingFrom('offering', targets),
    [handlePendingFrom]
  );
  const handleDocumentPending = useCallback(
    targets => handlePendingFrom('document', targets),
    [handlePendingFrom]
  );

  const isPendingReady = pendingReady.offering && pendingReady.document;

  const statusClarificationProgress =
    localProgress !== null ? localProgress : statusProgressProp;
  const isProgressOpen = isIcClarificationEditable(statusClarificationProgress);
  const isRespondClosed = isIcClarificationLocked(statusClarificationProgress);

  useEffect(() => {
    if (!focusRespondId) return undefined;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`ic-respond-${focusRespondId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [focusRespondId, activeTab, focusToken]);

  const handleOpenConfirm = () => {
    if (!isProgressOpen) return;
    if (!isPendingReady) return;
    if (livePendingTargets.length) {
      const first = livePendingTargets[0];
      setHighlightArmed(true);
      setActiveTab(first.tab);
      setFocusRespondId(first.id);
      setFocusToken(token => token + 1);
      notify.warning(
        'All evaluator responses must be replied to before submitting clarification. Reply to the highlighted response first.'
      );
      return;
    }
    setHighlightArmed(false);
    setFocusRespondId(null);
    if (!idOffer) {
      notify.error('Offer ID is missing. Refresh the page and try again.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    const success = await submitIcClarification();
    if (success) {
      setLocalProgress(CLARIFICATION_PROGRESS.WAITING_PANITIA);
      onProgressChange?.(CLARIFICATION_PROGRESS.WAITING_PANITIA);
      setConfirmOpen(false);
    }
  };

  const isResolvingIdentity = isLoadingProcurement || isLoadingIdentity;
  const showEmptyIdentity = !isResolvingIdentity && !procurementMitraId;

  const renderTabContent = (TabComponent, tabProps) => {
    if (isResolvingIdentity) {
      return <SkeletonText lines={6} className='py-3' />;
    }
    if (showEmptyIdentity) {
      return (
        <div className='text-center text-muted py-4 fs-13'>
          Offer identity was not found for the current partner.
        </div>
      );
    }
    return <TabComponent {...tabProps} />;
  };

  return (
    <>
      <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3'>
        <div>
          <h5 className='mb-1 fw-bold text-dark'>Clarification Detail</h5>
          <p className='text-muted mb-0 fs-13'>
            Review and respond to clarification notes for this offer.
          </p>
        </div>
        <DateCountdown
          label='Validity date'
          deadline={procurement?.proposalValidityPeriodMax}
          openLabel='Validity ends in'
          closedLabel='Validity ended'
        />
      </div>

      <Card>
        <CardBody className='checkout-tab'>
          <Nav tabs className='nav-tabs-custom nav-primary mt-1 mb-3'>
            {MENU_TABS.map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  tag='button'
                  type='button'
                  className={classnames('cursor-pointer', {
                    active: activeTab === tab.id,
                  })}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={`${tab.icon} me-1 align-bottom`}></i>
                  <span className='fw-semibold'>{tab.label}</span>
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId='offering'>
              {renderTabContent(OfferingTab, {
                procurement,
                procurementId,
                offerId: idOffer,
                procurementMitraId,
                isEvaluatorView: false,
                isRespondClosed,
                highlightArmed,
                onPendingRespondIds: handleOfferingPending,
                onRefreshProcurement,
              })}
            </TabPane>
            <TabPane tabId='document-attachment'>
              {renderTabContent(DocumentAttachmentTab, {
                procurementId,
                offerId: idOffer,
                procurementMitraId,
                isEvaluatorView: false,
                isRespondClosed,
                highlightArmed,
                onPendingRespondIds: handleDocumentPending,
              })}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>

      {isProgressOpen && (
        <div className='req-actionbar flex-wrap d-print-none'>
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            disabled={
              isSaving || isResolvingIdentity || !idOffer || !isPendingReady
            }
            loading={savingAction === 'submitClarification'}
            onClick={handleOpenConfirm}
          >
            Submit Clarification
          </ActionButton>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        title='Submit clarification?'
        message='You are about to submit your clarification responses. After submission, the evaluator will be notified, status will move to Waiting Response from Panitia Tender, and this clarification will become view-only until the evaluator sends it back or closes it.'
        confirmLabel='Submit clarification'
        isSubmitting={isSaving}
      />
    </>
  );
};

export default Clarification;
